/**
 * E-GTCC-VIEW-DPLANE1 — "Data Plane", the M2 OBSERVABILITY view that merges
 * the old console's DB Health Center, DB Ops Control Plane + Plan Builders and
 * DB Monitoring & Alerts into one place.
 *
 * Reference: `GTCC_REFERENCE_2026-09-01.html:918-957` (head, `.sub`, the four
 * `.tab`s at `font-size:12px`, `.grid-main`). The reference head is an `h1`;
 * inside GTCC the shell owns the page `h1` (`GtccShell.tsx:186`), so the view
 * head is an `h2` carrying the reference's head type scale — the same
 * deviation the cockpit already made (`GtccCockpitView.tsx:981`).
 *
 * Registry keys `dplane` and `dbops` both resolve here; the two old views stay
 * on disk until D-GTCC-SHELL1 merges.
 *
 * Read-only: nothing in this view executes a plan, a move or any other write.
 * The Plan Builders tab validates (`mode: "dry_run"`) and nothing else; the
 * Owner-Go execution paths stay in the old console under gtcc1.
 */
import { Stack, Tabs, type TabItem } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState, type ReactNode } from "react";

import { DPLANE_OVERVIEW_KEY, fetchDplaneOverview } from "../../api/dplane.js";
import { FreshBadge } from "../../ui/primitives.js";
import { ControlHead } from "../control/ControlPrimitives.js";
import {
  DPLANE_QUERY_OPTIONS,
  TAB_BAR_STYLE,
  type DplaneBuilderId,
} from "./components/DplanePanels.js";
import { DbMonitoringTab } from "./tabs/DbMonitoringTab.js";
import { HealthTab } from "./tabs/HealthTab.js";
import { InventoryTab } from "./tabs/InventoryTab.js";
import { PlansTab } from "./tabs/PlansTab.js";

/* ------------------------------------------------------------------ tabs */

/** The four `.tab`s of `GTCC_REFERENCE_2026-09-01.html:920`, in reference order. */
const DPLANE_TAB_IDS = ["health", "inventory", "plans", "monitoring"] as const;
type DplaneTabId = (typeof DPLANE_TAB_IDS)[number];
const DEFAULT_DPLANE_TAB: DplaneTabId = "health";
const TAB_ID_PREFIX = "gtcc-dplane";

function isDplaneTabId(value: string): value is DplaneTabId {
  return (DPLANE_TAB_IDS as ReadonlyArray<string>).includes(value);
}

/** Mirrors `initialTabFromQuery()` in `GtccCockpitView.tsx:95`. */
function initialTabFromQuery(): DplaneTabId {
  if (typeof window === "undefined") return DEFAULT_DPLANE_TAB;
  const requested = new URLSearchParams(window.location.search).get("tab");
  if (requested !== null && isDplaneTabId(requested)) return requested;
  return DEFAULT_DPLANE_TAB;
}

function writeTabToQuery(next: DplaneTabId): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set("tab", next);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

/* ------------------------------------------------------------------ head */

/*
 * E-GTCC-VIEW-DPLANE-GW-HW1, Ursache 2 (Zeilenraster): hier standen ein
 * `VIEW_STYLE` mit `padding: var(--gt-sys-space-5)` und eigene `HEAD_STYLE` /
 * `HEAD_TITLE_STYLE`. Die Polsterung hat die ganze Sicht um 24 px nach unten
 * und nach rechts geschoben — gemessen stand der Titel bei y=95/x=294 gegen
 * y=71/x=270 der Referenz, und damit jede Zeile darunter daneben. Das Deck
 * traegt seinen Rahmen in der Shell; `gw` und `hw` setzen deshalb nur
 * `Stack gap={3}` und treffen den Titel der Referenz genau. Kopf und
 * Unterzeile kommen jetzt aus `ControlHead`
 * (`views/control/ControlPrimitives.tsx`) — derselbe Baustein, dieselben
 * Test-Kennungen `gtcc-dplane-title` / `gtcc-dplane-sub`, kein lokaler Wert.
 */

/**
 * `.fresh` of the reference head. The age is never computed from the local
 * clock: the data-plane overview carries no `generatedAt`, so the newest
 * reachability probe timestamp (`DbReachabilityHealth.lastCheckedAt`,
 * `data-plane/types.ts:125`) is rendered as the measurement time. Without a
 * single probe row there is no measurement, and the badge reads
 * "nicht messbar" instead of a number (Owner decisions P9/P10).
 */
/**
 * `NotMeasurable` and `FleetFreshBadge` (`FleetPrimitives.tsx`) have a
 * shared-kit equivalent now (`FreshBadge`, `ui/primitives.tsx`), but it
 * carries no `data-testid`; these two locals reproduce the old i18n label
 * computation and grey "nicht messbar" markup while keeping the
 * `gtcc-dplane-fresh` testid `DplaneView.test.tsx` asserts on — the same
 * call-site adaptation `LaunchView.tsx` uses for `Dot`.
 */
function DplaneFreshUnmeasured(props: { testId: string }): ReactNode {
  const { t } = useLocale();
  return (
    <span className="mono muted" data-testid={props.testId}>
      {t("admin.gtcc.cockpit.infra.not_measurable")}
    </span>
  );
}

function DplaneFreshMeasured(props: { measuredAt: string; testId: string }): ReactNode {
  const { t, locale } = useLocale();
  const label = t("admin.gtcc.cockpit.infra.fresh.measured_at", {
    time: new Intl.DateTimeFormat(locale, { timeStyle: "medium" }).format(
      new Date(props.measuredAt),
    ),
  });
  return (
    <span data-testid={props.testId}>
      <FreshBadge label={label} />
    </span>
  );
}

function DplaneFreshBadge(): ReactNode {
  const overview = useQuery({
    queryKey: DPLANE_OVERVIEW_KEY,
    queryFn: ({ signal }) => fetchDplaneOverview(signal),
    ...DPLANE_QUERY_OPTIONS,
  });
  const data = overview.data;
  if (data === undefined || !data.ok) return <DplaneFreshUnmeasured testId="gtcc-dplane-fresh" />;
  const checks = data.data.reachability ?? [];
  let newest: string | undefined;
  for (const check of checks) {
    if (newest === undefined || check.lastCheckedAt > newest) newest = check.lastCheckedAt;
  }
  if (newest === undefined) return <DplaneFreshUnmeasured testId="gtcc-dplane-fresh" />;
  return <DplaneFreshMeasured measuredAt={newest} testId="gtcc-dplane-fresh" />;
}

/* ------------------------------------------------------------------ view */

export function DplaneView(): ReactNode {
  const { t, direction } = useLocale();
  const [activeTab, setActiveTab] = useState<DplaneTabId>(initialTabFromQuery);
  const [focusBuilder, setFocusBuilder] = useState<DplaneBuilderId | undefined>(undefined);
  const [focusTenantId, setFocusTenantId] = useState<string | undefined>(undefined);

  // D-GTCC-FRAME1/D-GTCC-ACCEPT-SHELL1 — the Shell's breadcrumb reads the
  // active tab from `?tab=` (GtccShell.tsx) to show it as the 3rd segment;
  // without the query param a fresh load (no `?tab=` yet) leaves the
  // breadcrumb one level short even though a tab is in fact active. Writing
  // the resolved id once on mount keeps the URL truthful without changing
  // any rendered content (mirrors GtccCockpitView.tsx:654).
  useEffect(() => {
    writeTabToQuery(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTabChange = useCallback((next: string) => {
    if (!isDplaneTabId(next)) return;
    setActiveTab(next);
    writeTabToQuery(next);
  }, []);

  /**
   * The `[Move-Plan (dry-run)]` and `[Validate]` affordances of the Health
   * Center. They navigate — they never send anything.
   */
  const onOpenPlanBuilder = useCallback((builder: DplaneBuilderId, tenantId?: string) => {
    setFocusBuilder(builder);
    setFocusTenantId(tenantId);
    setActiveTab("plans");
    writeTabToQuery("plans");
  }, []);

  const items: ReadonlyArray<TabItem> = DPLANE_TAB_IDS.map((id) => ({
    id,
    label: t(`admin.gtcc.dplane.tab.${id}`),
    testId: `${TAB_ID_PREFIX}-tab-${id}`,
  }));

  return (
    <Stack gap={3} data-testid="gtcc-view-dplane">
      {/*
       * Kopf und `.fresh` stehen im Referenz-Markup nebeneinander im selben
       * Flex-Kasten, nicht ineinander. Das ist auch die barrierefreie Form: im
       * Ueberschriftselement wuerde das Badge in den Accessible Name der
       * Ueberschrift wandern ("Data Plane nicht messbar"), und jede Suche nach
       * der Ueberschrift "Data Plane" ginge ins Leere. `ControlHead` baut genau
       * diesen Kasten.
       */}
      <ControlHead
        title={t("admin.gtcc.dplane.title")}
        subtitle={t("admin.gtcc.dplane.subtitle")}
        fresh={<DplaneFreshBadge />}
        testId="gtcc-dplane"
      />
      <div style={TAB_BAR_STYLE}>
        <Tabs
          items={items}
          value={activeTab}
          onChange={onTabChange}
          ariaLabel={t("admin.gtcc.dplane.tabs_aria")}
          idPrefix={TAB_ID_PREFIX}
          direction={direction}
          variant="underline"
          dataTestId="gtcc-dplane-tablist"
        />
      </div>
      <div
        role="tabpanel"
        id={`${TAB_ID_PREFIX}-panel-${activeTab}`}
        aria-labelledby={`${TAB_ID_PREFIX}-tab-${activeTab}`}
        data-testid={`gtcc-dplane-panel-${activeTab}`}
      >
        {activeTab === "health" ? (
          <HealthTab direction={direction} onOpenPlanBuilder={onOpenPlanBuilder} />
        ) : null}
        {activeTab === "inventory" ? (
          <InventoryTab direction={direction} onOpenPlanBuilder={onOpenPlanBuilder} />
        ) : null}
        {activeTab === "plans" ? (
          <PlansTab
            direction={direction}
            onOpenPlanBuilder={onOpenPlanBuilder}
            focusBuilder={focusBuilder}
            focusTenantId={focusTenantId}
          />
        ) : null}
        {activeTab === "monitoring" ? (
          <DbMonitoringTab direction={direction} onOpenPlanBuilder={onOpenPlanBuilder} />
        ) : null}
      </div>
    </Stack>
  );
}
